import { useEffect, useRef } from "react"
import { UseFormSetValue, UseFormWatch } from "react-hook-form"
import { TSale } from "../types/Sale"
import { findCountry } from "../phone"
import { detectCountryFromLocale } from "../phone/detectLocale"
import { useCheckoutContext } from "./useCheckoutContext"
import { useGatewayContext } from "../contexts/GatewayContext"

/**
 * Mantém o DDI do celular alinhado ao país de cobrança (venda internacional).
 *
 * - No carregamento, o país de cobrança (que já vem do IP) define o DDI inicial;
 *   sem venda internacional, ou com país fora do registry de DDI, vale o locale do navegador.
 * - Quando o comprador troca o país de cobrança, o DDI acompanha só se o celular
 *   ainda estiver vazio, para não apagar nem reinterpretar o que ele digitou.
 * - O caminho inverso não existe: trocar o DDI nunca altera o país de cobrança.
 */
export const useBuyerCountryPhoneSync = (
  watch: UseFormWatch<TSale>,
  setValue: UseFormSetValue<TSale>,
) => {
  const { productPrice } = useCheckoutContext()
  const { buyerCountry } = useGatewayContext()
  const enabled = productPrice?.internationalSalesEnabled === true

  const phone = watch("phone")
  const phoneRef = useRef(phone)
  phoneRef.current = phone

  const initialized = useRef(false)

  useEffect(() => {
    const iso = (buyerCountry ?? "").toUpperCase()
    const canSync = enabled && iso !== "" && findCountry(iso) !== undefined

    if (!initialized.current) {
      initialized.current = true
      if (canSync) {
        setValue("phoneCountry", iso)
        return
      }
      const detected = detectCountryFromLocale(navigator.language)
      if (detected !== "BR") {
        setValue("phoneCountry", detected)
      }
      return
    }

    if (canSync && !phoneRef.current) {
      setValue("phoneCountry", iso)
    }
  }, [enabled, buyerCountry, setValue])
}

export default useBuyerCountryPhoneSync
